import { useState } from 'react'
import { CHART_COLORS } from '../utils/chartColors'

const C = {
  bg: 'transparent',
  border: CHART_COLORS.border,
  text: CHART_COLORS.text,
  textSecond: CHART_COLORS.textMuted,
  primary: CHART_COLORS.primary,
  hover: CHART_COLORS.primaryFade,
}

/** Explicit placement for a cell in an asymmetric grid (1-based grid lines). */
export interface ChartLayoutCell {
  rowStart: number
  colStart: number
  rowSpan?: number
  colSpan?: number
}

export interface ChartLayout {
  rows: number
  cols: number
  /** Explicit cell placements for asymmetric layouts (e.g. 1-top/2-bottom). */
  cells?: readonly ChartLayoutCell[]
  /** Stable id — disambiguates asymmetric presets that share rows/cols. */
  id?: string
  /** Human-readable label used for the preset's aria-label. */
  label?: string
}

/** Stable key for a preset — its id when set, else `${rows}x${cols}`. */
function presetKey(layout: ChartLayout): string {
  return layout.id ?? `${layout.rows}x${layout.cols}`
}

/** Active-state equality: id-based when either side carries one, else rows/cols. */
function layoutsMatch(a: ChartLayout, b: ChartLayout): boolean {
  if (a.id || b.id) return a.id === b.id
  return a.rows === b.rows && a.cols === b.cols
}

export const DEFAULT_LAYOUT_PRESETS: readonly ChartLayout[] = [
  { rows: 1, cols: 1 },
  { rows: 1, cols: 2 },
  { rows: 2, cols: 1 },
  { rows: 1, cols: 3 },
  { rows: 3, cols: 1 },
  { rows: 2, cols: 2 },
  { rows: 2, cols: 3 },
  { rows: 3, cols: 2 },
  { rows: 3, cols: 3 },
] as const

interface ChartLayoutPickerProps {
  layout: ChartLayout
  onLayoutChange: (layout: ChartLayout) => void
  presets?: readonly ChartLayout[]
}

/** Presentational layout-preset picker for the chart settings menu. */
export function ChartLayoutPicker({
  layout,
  onLayoutChange,
  presets = DEFAULT_LAYOUT_PRESETS,
}: ChartLayoutPickerProps) {
  return (
    <div
      data-testid="chart-layout-picker"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
      }}
    >
      {presets.map((preset) => (
        <PresetButton
          key={presetKey(preset)}
          preset={preset}
          active={layoutsMatch(preset, layout)}
          onSelect={() => onLayoutChange(preset)}
        />
      ))}
    </div>
  )
}

function PresetButton({
  preset,
  active,
  onSelect,
}: {
  preset: ChartLayout
  active: boolean
  onSelect: () => void
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      data-testid={`layout-preset-${presetKey(preset)}`}
      aria-pressed={active}
      aria-label={preset.label ?? `${preset.rows} by ${preset.cols} grid`}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 48,
        width: 48,
        padding: 4,
        borderRadius: 6,
        border: `1px solid ${active ? C.primary : C.border}`,
        background: active ? C.hover : hover ? C.hover : C.bg,
        color: active ? C.primary : C.text,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      <LayoutPreview layout={preset} />
    </button>
  )
}

function LayoutPreview({ layout }: { layout: ChartLayout }) {
  // Asymmetric presets place each tile explicitly; uniform presets auto-flow.
  const cellCount = layout.cells ? layout.cells.length : layout.rows * layout.cols
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
        gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
        gap: 2,
        height: '100%',
        width: '100%',
      }}
    >
      {Array.from({ length: cellCount }).map((_, i) => {
        const cell = layout.cells?.[i]
        return (
          <div
            key={i}
            data-testid="layout-preview-tile"
            style={{
              background: 'currentColor',
              opacity: 0.6,
              borderRadius: 2,
              gridColumn: cell ? `${cell.colStart} / span ${cell.colSpan ?? 1}` : undefined,
              gridRow: cell ? `${cell.rowStart} / span ${cell.rowSpan ?? 1}` : undefined,
            }}
          />
        )
      })}
    </div>
  )
}
